import { useCallback, useEffect, useState } from 'react';

/**
 * Installation der PWA auf dem Gerät (SPEC Kap. 13).
 *
 * Vor Ort läuft die App als installierte Anwendung — ohne Adressleiste, mit
 * eigenem Symbol und im Vollbild. Chrome und Edge meldet dafür einmalig
 * `beforeinstallprompt`; das Ereignis muss aufbewahrt werden, weil der Browser
 * es nicht wiederholt. Safari kennt es nicht: Dort führt nur „Teilen → Zum
 * Home-Bildschirm" zum Ziel, deshalb gibt es dafür eine Anleitung statt eines
 * Knopfes.
 */

/** `beforeinstallprompt` steht in den TypeScript-Standardtypen nicht. */
interface InstallationsEreignis extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallationsWeg = 'knopf' | 'anleitung' | 'keiner';

export interface Installationszustand {
  /** Läuft die App bereits als installierte Anwendung? */
  installiert: boolean;
  /** `knopf`: Browser fragt selbst. `anleitung`: nur über das Teilen-Menü. */
  weg: InstallationsWeg;
  /** Öffnet den Browserdialog. Ergebnis: hat der Nutzer zugestimmt? */
  installieren: () => Promise<boolean>;
}

const alsStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const anzeige =
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches);
  // Safari auf iOS kennt `display-mode` nicht und setzt stattdessen dieses Flag.
  const safari = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return anzeige || safari;
};

/** iOS und iPadOS — dort gibt es keinen Installationsdialog. */
const istApfelGeraet = (): boolean => {
  if (typeof window === 'undefined') return false;
  const kennung = window.navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(kennung)) return true;
  // iPadOS meldet sich seit Version 13 als Mac — nur die Touchpunkte verraten es.
  return /Macintosh/.test(kennung) && window.navigator.maxTouchPoints > 1;
};

export const useInstallation = (): Installationszustand => {
  const [ereignis, setEreignis] = useState<InstallationsEreignis | null>(null);
  const [installiert, setInstalliert] = useState(alsStandalone);

  useEffect(() => {
    const merken = (roh: Event): void => {
      // Ohne `preventDefault` zeigt Chrome eine eigene Leiste; die Einladung
      // gehört an eine Stelle, die zur App passt.
      roh.preventDefault();
      setEreignis(roh as InstallationsEreignis);
    };
    const fertig = (): void => {
      setInstalliert(true);
      setEreignis(null);
    };

    window.addEventListener('beforeinstallprompt', merken);
    window.addEventListener('appinstalled', fertig);
    return () => {
      window.removeEventListener('beforeinstallprompt', merken);
      window.removeEventListener('appinstalled', fertig);
    };
  }, []);

  const installieren = useCallback(async (): Promise<boolean> => {
    if (!ereignis) return false;
    await ereignis.prompt();
    const { outcome } = await ereignis.userChoice;
    // Das Ereignis ist verbraucht — ein zweiter Aufruf würde fehlschlagen.
    setEreignis(null);
    if (outcome === 'accepted') setInstalliert(true);
    return outcome === 'accepted';
  }, [ereignis]);

  const weg: InstallationsWeg = installiert
    ? 'keiner'
    : ereignis
      ? 'knopf'
      : istApfelGeraet()
        ? 'anleitung'
        : 'keiner';

  return { installiert, weg, installieren };
};
